import React, { useState } from 'react';

import { useBaseTable } from 'hooks/useBaseTable';
import { useBaseWidget } from 'hooks/useBaseWidget';

import { PeriodItemEntry } from 'api/queries/getSummaryTrackedActivities.query';

import { CustomizedTabs } from 'components/common/CustomizedTabs/CustomizedTabs';
import { TooltipPosition } from 'components/common/CustomizedTooltip/customizedTooltip.enums';
import { ExportDropdown } from 'components/common/ExportDropdown/exportDropdown';
import Icon from 'components/common/Icons/icon';
import { CustomTableColumnsModal } from 'components/common/Modals/CustomTableColumns/customTableColumnsModal';
import { InputOption, ValueContainer } from 'components/common/MyCustomSelect/myCustomSelect';
import NoData from 'components/common/NoData/noData';
import OverlayWithSpinner from 'components/common/OverlayWithSpinner/overlayWithSpinner';
import { ShareInsightButton } from 'components/common/ShareInsightButton/ShareInsightButton';
import SwitchComponent from 'components/common/Switch/Switch';
import { TableV8 } from 'components/common/Tables/TableV8/tableV8';
import { WidgetWithTitle } from 'components/common/WidgetWithTitle/WidgetWithTitle';

import { AddWidget } from 'components/Dashboards/Project/Components/CustomDashboard/AddRemoveWidget/addWidget';
import { PinboardWidgetPills } from 'components/Dashboards/Project/Components/CustomDashboard/PinboardWidgetPills/PinboardWidgetPills';
import { ITableWidgetWrapperProps } from 'components/Dashboards/Project/Components/CustomDashboard/utils/utils.type';
import { TableFooter } from 'components/Dashboards/Project/Components/VersionCompare/TableFotter/TableFooter';
import { TKeys } from 'components/Dashboards/Project/Components/VersionCompare/utils/helper';
import { smallPageSize } from 'components/Dashboards/Project/Components/VersionCompare/utils/table.utils';
import { projectConfig } from 'components/Dashboards/Project/ProjectWrapper/projectConfig';
import { CustomizedRoundIcon, CustomizedTooltip, MyCustomSelect } from 'components/common';
import { TableLocalFilters } from 'components/widgets/ChangeAnalysisTrackedActivities/tableLocalFilters/tableLocalFilters';
import { statusOptions } from 'components/widgets/KeyActivities/components/filter.utils';
import { useStartDateChangesLogic } from 'components/widgets/TopStartDateChanges/startDateChanges.logic';
import classes from 'components/widgets/TopStartDateChanges/startDateChanges.module.scss';
import { useStartDateChangesProvider } from 'components/widgets/TopStartDateChanges/startDateChanges.provider';
import { columnsV8, initialState } from 'components/widgets/TopStartDateChanges/startDateChanges.utils';

const title = 'Top Start Date Changes';
const elementId = 'start-date-changes';
const id = 'topStartDateChanges';
const componentKey = 'topStartDateChanges';

const StartDateChanges = ({
    projectId,
    contractId,
    widgetId,
    latestVersionId,
    compareVersionId,
    globalFilterData,
    isSinglePage = false,
    externalComponents = null,
    editNarrative = null,
    setSubTitleForExport,
    localFilters,
}: ITableWidgetWrapperProps) => {
    const { route, subTitle } = useBaseWidget({
        widgetId,
        id,
        title,
        setSubTitleForExport,
        latestVersionId,
        contractId,
        projectId,
    });

    const { table } = useBaseTable({
        widgetId,
        localFilters,
        componentKey,
        initialState,
        columns: columnsV8,
    });

    const { ui, api } = useStartDateChangesProvider({
        latestVersionId,
        compareVersionId,
        contractId,
        widgetId,
        localFilters,
        globalFilterData,
        projectId,
        table,
    });

    const { logic } = useStartDateChangesLogic({ ui, api, table, title, subTitle, isSinglePage });
    return (
        <WidgetWithTitle
            title={title}
            tooltip={api.getStartDateChanges.data && api.getStartDateChanges.data.info}
            id={id}
            titleFilters={[
                editNarrative && <div key={'editNarrative'}>{editNarrative}</div>,
                widgetId && ui.isInPinBoard && ui.pills.length > 0 ? (
                    <PinboardWidgetPills key={'widgetFilters'} widgetId={widgetId} filters={ui.pills} />
                ) : (
                    <TableLocalFilters key={'filters'}>
                        <div className={classes.selectWrapper}>
                            <div className={classes.title}>Status</div>
                            <MyCustomSelect<ISelectOption<string>, true>
                                value={ui.state.statusOptions}
                                onChange={(e) => ui.setters.setStatusOptions([...e])}
                                options={statusOptions}
                                isClearable={true}
                                closeMenuOnSelect={false}
                                hideSelectedOptions={false}
                                isMulti={true}
                                placeholder={'Select Status...'}
                                components={{
                                    Option: InputOption,
                                    ValueContainer: ValueContainer,
                                }}
                            />
                        </div>
                        <div>
                            Critical Path Only
                            <SwitchComponent
                                checked={ui.state.isCriticalPath}
                                onChange={() => {
                                    ui.setters.setIsCriticalPath(!ui.state.isCriticalPath);
                                }}
                            />
                        </div>
                    </TableLocalFilters>
                ),
            ]}
            titleComponents={[
                externalComponents && <div key={'externalComponents'}>{externalComponents}</div>,
                <CustomizedTooltip
                    key={'tableColumns'}
                    tooltipPosition={TooltipPosition.Top}
                    tooltipContent={'Customize table columns'}
                    triggerElement={
                        <CustomizedRoundIcon enableHover={true} onClick={() => table.setModalColumnsOpen(true)}>
                            <Icon name={'tune-filter'} />
                        </CustomizedRoundIcon>
                    }
                />,
                <AddWidget
                    key={'AddWidget'}
                    componentKey={componentKey}
                    title={title}
                    projectId={projectId}
                    contractId={contractId}
                    elementId={elementId}
                    widgetId={widgetId}
                    route={route}
                    localFilters={ui.state}
                    callBack={table.setWidgetColumns}
                />,
                <ShareInsightButton key={'shareInsight'} title={title} link={route} />,
                <ExportDropdown
                    title={title}
                    key={'export'}
                    subTitle={subTitle}
                    handleAPIExcelExport={logic.handleAPIExcelExport}
                />,
            ]}
        >
            {logic.filteredData && (
                <div className={classes.tableWrapperContainer}>
                    {logic.tabsArrData?.length > 0 && (
                        <CustomizedTabs<TKeys>
                            tabs={logic.tabsArrData}
                            activeTab={ui.state.activeTab}
                            setActiveTab={ui.setters.setActiveTab}
                        />
                    )}

                    {table.updatedInitialState && (
                        <TableV8<PeriodItemEntry>
                            data={logic.filteredData}
                            columns={columnsV8}
                            isLoading={api.getStartDateChanges.isLoading || api.getStartDateChanges.isFetching}
                            noData={<NoData>No Changes To Display</NoData>}
                            sorting={table.sorting}
                            setSorting={table.setSorting}
                            columnVisibility={table.columnVisibility}
                            setColumnVisibility={table.setColumnVisibility}
                            columnOrder={table.columnOrder}
                            setColumnOrder={table.setColumnOrder}
                            manualPagination={false}
                            manualSorting={false}
                            pagination={table.pagination}
                            rowSelection={table.rowSelection}
                            setRowSelection={table.setRowSelection}
                        />
                    )}
                    {logic.filteredData.length > smallPageSize && !isSinglePage && (
                        <TableFooter
                            setPageSize={logic.setPageSize}
                            pageSize={logic.pageSize}
                            link={!widgetId ? projectConfig.startDateChanges.link : undefined}
                        />
                    )}
                </div>
            )}

            <CustomTableColumnsModal
                onHideModal={() => table.setModalColumnsOpen(false)}
                showModal={table.modalColumnsOpen}
                widgetId={widgetId}
                componentKey={componentKey}
                tableInstance={table.tableInstance}
                initialState={initialState}
                columns={columnsV8}
                onChangeOrder={table.setColumnOrder}
                onChangeVisibility={table.setColumnVisibility}
            />

            {logic.spinnerOpen && <OverlayWithSpinner />}
        </WidgetWithTitle>
    );
};
export default React.memo(StartDateChanges);
